import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type RubricDraftStatusBarProps = {
  revisionVersion?: number
  validationMessage?: string | null
  hasUnsavedChanges: boolean
  publishPending?: boolean
  canPublish: boolean
  onPreview: () => void
  onPublish: () => void
  className?: string
}

export function RubricDraftStatusBar({
  revisionVersion,
  validationMessage,
  hasUnsavedChanges,
  publishPending,
  canPublish,
  onPreview,
  onPublish,
  className,
}: RubricDraftStatusBarProps) {
  if (!hasUnsavedChanges && !validationMessage) return null

  return (
    <div
      className={cn(
        'sticky bottom-0 z-10 -mx-4 border-t bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:-mx-6 sm:px-6',
        hasUnsavedChanges ? 'border-warning/40' : 'border-border',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 space-y-0.5 text-sm">
          {hasUnsavedChanges ? (
            <p className="font-medium text-warning">
              Unpublished changes
              {revisionVersion ? ` · based on v${revisionVersion}` : ''}
            </p>
          ) : null}
          {validationMessage ? (
            <p className="text-warning">{validationMessage}</p>
          ) : hasUnsavedChanges ? (
            <p className="text-muted-foreground">
              Publish a new revision to apply changes to analysis reports.
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Button type="button" variant="outline" size="sm" onClick={onPreview}>
            Preview report
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onPublish}
            disabled={!canPublish || publishPending || Boolean(validationMessage)}
          >
            {publishPending ? 'Publishing…' : 'Publish revision'}
          </Button>
        </div>
      </div>
    </div>
  )
}
